import { DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tooltip, Typography } from "antd";
import { Link } from "react-router-dom";
import { InternalInvoiceItem } from "@/models/internal-invoice";
import { RemittanceType } from "@/models/enum/remittance-type";
import { renderCurrency, renderDate, renderPercentage } from "@/utils";
import { exportInternalInvoiceItemsCsv } from "./export-internal-invoice-items-csv";

const { Text, Title } = Typography;

const columns = [
  {
    title: "Payee Code",
    key: "payeeCode",
    dataIndex: "payeeCode",
    width: "100px",
  },
  {
    title: "Account Name",
    key: "accountName",
    dataIndex: "accountName",
    width: "200px",
  },
  {
    title: "Remittance Number",
    key: "remittanceNumber",
    dataIndex: "remittanceNumber",
    width: "100px",
    render: (value: string) => (
      <Link to={`/remittances/${value}`}>{value}</Link>
    ),
  },
  {
    title: "Invoice Number",
    key: "invoiceNumber",
    dataIndex: "invoiceNumber",
    width: "100px",
    render: (value: string, row: InternalInvoiceItem) => {
      switch (row.type) {
        case RemittanceType.INVOICE:
          return (
            <Link to={`/invoices/${row.payeeCode}/${encodeURIComponent(value)}`}>
              {value}
            </Link>
          );
        case RemittanceType.SC:
        case RemittanceType.SCR:
        case RemittanceType.PC:
        case RemittanceType.PCR: {
          const pureInvoiceNumber = value.replace(/(SC|SCR|PC|PCR)+$/g, "");
          return (
            <Link to={`/invoices/${row.payeeCode}/${encodeURIComponent(pureInvoiceNumber)}`}>
              {value}
            </Link>
          );
        }
        default:
          return <Text>{value}</Text>;
      }
    },
  },
  {
    title: "Invoice Date",
    key: "invoiceDate",
    dataIndex: "invoiceDate",
    width: "70px",
    render: renderDate,
  },
  {
    title: "Payment Due Date",
    key: "paymentDueDate",
    dataIndex: "paymentDueDate",
    width: "70px",
    render: renderDate,
  },
  {
    title: "Fee",
    key: "fee",
    dataIndex: "fee",
    width: "70px",
    render: renderPercentage,
  },
  {
    title: "Recovered Amount",
    key: "recoveredAmount",
    dataIndex: "recoveredAmount",
    width: "100px",
    render: renderCurrency,
  },
  {
    title: "Amount",
    key: "amount",
    dataIndex: "amount",
    width: "100px",
    render: renderCurrency,
  },
];

export function InternalInvoiceItemsTable(props: {
  items: InternalInvoiceItem[];
  title?: string;
  exportFilename?: string;
  exportButtonSize?: "small" | "middle" | "large";
}) {
  return (
    <>
      {props.title && (
        <Space align="center" >
          <Title level={4} style={{ margin: 0 }}>
            {props.title}
          </Title>
          {props.exportFilename && (
            <Tooltip title="Export details">
              <Button
                shape="circle"
                size={props.exportButtonSize ?? "small"}
                icon={<DownloadOutlined />}
                disabled={!props.items.length}
                onClick={() =>
                  exportInternalInvoiceItemsCsv(props.items, props.exportFilename!)
                }
              />
            </Tooltip>
          )}
        </Space>
      )}
      <Table
        columns={columns}
        dataSource={props.items}
        rowKey="invoiceNumber"
        size="small"
        pagination={{ position: ["bottomCenter"] }}
        scroll={{ x: 1000 }}
      />
    </>
  );
}
